"use client"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { StepProps } from "@/types/listing"
import { useState, useEffect, useMemo, useCallback } from "react"
import { motion } from "framer-motion"
import { ChevronRight, Loader2 } from "lucide-react"
import { useMobileInputScroll } from "@/hooks/use-mobile-input-scroll"
import SearchableDropdown from "@/components/ui/SearchableDropdown"
import type { Option } from "@/components/ui/SearchableDropdown"
import { CreationFlowInlinePicker } from "@/components/publications/creation-flow/inline-enum-picker"
import { shouldUseClassicPicker } from "@/lib/dropdown-constants"
import { creationFlowInputClass } from "@/lib/creation-flow-form-styles"

interface TaxonomyItem {
  id: string
  slug: string | null
  name: string
}

const TAXONOMY_LIMIT = 100

async function fetchTaxonomy(
  type: string,
  q?: string,
  parentId?: string | null
): Promise<TaxonomyItem[]> {
  const params = new URLSearchParams({ type })
  if (q?.trim()) params.set("q", q.trim())
  if (parentId) params.set("parent_id", parentId)
  params.set("limit", String(TAXONOMY_LIMIT))
  const res = await fetch(`/api/taxonomy?${params.toString()}`)
  if (!res.ok) return []
  const json = await res.json()
  return Array.isArray(json.items) ? json.items : []
}

function toOptions(items: TaxonomyItem[], otherLast = false): Option[] {
  if (!otherLast) {
    return items.map((item) => ({ code: item.id, name: item.name, flag: "" }))
  }
  const rest = items.filter((item) => item.name.toLowerCase() !== "other")
  const other = items.filter((item) => item.name.toLowerCase() === "other")
  return [...rest, ...other].map((item) => ({ code: item.id, name: item.name, flag: "" }))
}

function findOption(options: Option[], id: string | null | undefined): Option | null {
  if (!id) return null
  const match = options.find((option) => option.code === id)
  return match ?? null
}

export function CategoryStep({ data, updateDataAction, onNextAction, hideButton, onValidationChange, compact }: StepProps) {
  const tML = useTranslations("myListings");
  const [brands, setBrands] = useState<TaxonomyItem[]>([])
  const [themes, setThemes] = useState<TaxonomyItem[]>([])
  const [productTypes, setProductTypes] = useState<TaxonomyItem[]>([])

  const [selectedBrand, setSelectedBrand] = useState<string | null | undefined>(
    data.brand_id ?? undefined
  )
  const [selectedTheme, setSelectedTheme] = useState<string | null | undefined>(
    data.theme_id ?? undefined
  )
  const [selectedProductType, setSelectedProductType] = useState<string | null | undefined>(
    data.product_type_id ?? undefined
  )

  const [loading, setLoading] = useState(true)
  const [themesLoading, setThemesLoading] = useState(false)
  
  const [themeCustomText, setThemeCustomText] = useState("")
  const [productTypeCustomText, setProductTypeCustomText] = useState("")
  const customThemeInputRef = useMobileInputScroll<HTMLInputElement>()
  const productTypeCustomInputRef = useMobileInputScroll<HTMLInputElement>()
  useEffect(() => {
    setSelectedBrand(data.brand_id ?? undefined)
    setSelectedTheme(data.theme_id ?? undefined)
    setSelectedProductType(data.product_type_id ?? undefined)
    setThemeCustomText(data.theme_custom_text ?? "")
    setProductTypeCustomText(data.product_type_custom_text ?? "")
  }, [data.brand_id, data.theme_id, data.product_type_id, data.theme_custom_text, data.product_type_custom_text])

  useEffect(() => {
    let cancelled = false
    fetchTaxonomy("brand")
      .then((items) => {
        if (cancelled) return
        const legoOnly = items.filter(
          (b) => b.slug?.toLowerCase() === "lego" || b.name.toLowerCase() === "lego"
        )
        setBrands(legoOnly)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (brands.length === 0) return
    const lego = brands[0]
    const selectedIsValid = selectedBrand
      ? brands.some((b) => b.id === selectedBrand)
      : false
    if (selectedIsValid) return
    setSelectedBrand(lego.id)
    updateDataAction({
      brand_id: lego.id,
      theme_id: undefined,
      product_type_id: undefined,
      theme_custom_text: undefined,
      brand_custom_text: undefined,
      product_type_custom_text: undefined,
    })
  }, [brands, selectedBrand, updateDataAction])

  useEffect(() => {
    if (selectedBrand === undefined || !selectedBrand) {
      setThemes([])
      setThemesLoading(false)
      return
    }
    let cancelled = false
    setThemesLoading(true)
    fetchTaxonomy("theme", undefined, selectedBrand)
      .then((items) => {
        if (!cancelled) setThemes(items)
      })
      .finally(() => {
        if (!cancelled) setThemesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedBrand])

  useEffect(() => {
    if (selectedTheme === undefined || !selectedTheme) {
      setProductTypes([])
      return
    }
    let cancelled = false
    fetchTaxonomy("product_type", undefined, selectedTheme).then((items) => {
      if (!cancelled) setProductTypes(items)
    })
    return () => {
      cancelled = true
    }
  }, [selectedTheme])

  const isThemeOther = selectedTheme ? themes.find((t) => t.id === selectedTheme)?.name.toLowerCase() === "other" : false
  const isProductTypeOther = selectedProductType
    ? productTypes.find((p) => p.id === selectedProductType)?.name.toLowerCase() === "other"
    : false
  const isValid = Boolean(
    selectedBrand &&
      selectedTheme &&
      selectedProductType &&
      (!isThemeOther || themeCustomText.trim()) &&
      (!isProductTypeOther || productTypeCustomText.trim())
  )
  useEffect(() => {
    onValidationChange?.(isValid)
  }, [isValid, onValidationChange])

  const handleNext = () => {
    updateDataAction({
      brand_id: selectedBrand!,
      theme_id: selectedTheme!,
      product_type_id: selectedProductType!,
      theme_custom_text: isThemeOther ? themeCustomText.trim() : undefined,
      brand_custom_text: undefined,
      product_type_custom_text: isProductTypeOther ? productTypeCustomText.trim() : undefined,
    })
    onNextAction()
  }

  const handleBrandSelect = (brandId: string) => {
    setSelectedBrand(brandId)
    setSelectedTheme(undefined)
    setSelectedProductType(undefined)
    setThemeCustomText("")
    setProductTypeCustomText("")
    updateDataAction({
      brand_id: brandId,
      theme_id: undefined,
      product_type_id: undefined,
      theme_custom_text: undefined,
      brand_custom_text: undefined,
      product_type_custom_text: undefined,
    })
  }

  const handleThemeSelect = (themeId: string) => {
    const selectedThemeName = themes.find((t) => t.id === themeId)?.name.toLowerCase()
    if (selectedThemeName === "other") {
      setSelectedTheme(themeId)
      setSelectedProductType(undefined)
      setProductTypeCustomText("")
      updateDataAction({
        brand_id: selectedBrand ?? undefined,
        theme_id: themeId,
        product_type_id: undefined,
        theme_custom_text: themeCustomText.trim(),
        brand_custom_text: undefined,
        product_type_custom_text: undefined,
      })
    } else {
      setSelectedTheme(themeId)
      setSelectedProductType(undefined)
      setThemeCustomText("")
      updateDataAction({
        brand_id: selectedBrand ?? undefined,
        theme_id: themeId,
        product_type_id: undefined,
        theme_custom_text: undefined,
        brand_custom_text: undefined,
        product_type_custom_text: undefined,
      })
    }
  }

  const handleThemeCustomTextChange = (value: string) => {
    setThemeCustomText(value)
    updateDataAction({
      brand_id: selectedBrand ?? undefined,
      theme_id: selectedTheme ?? undefined,
      product_type_id: selectedProductType ?? undefined,
      theme_custom_text: value.trim() || undefined,
      brand_custom_text: undefined,
      product_type_custom_text: isProductTypeOther ? productTypeCustomText : undefined,
    })
  }

  const handleProductTypeSelect = (productTypeId: string) => {
    setSelectedProductType(productTypeId)
    updateDataAction({
      brand_id: selectedBrand ?? undefined,
      theme_id: selectedTheme ?? undefined,
      product_type_id: productTypeId,
      theme_custom_text: isThemeOther ? themeCustomText : undefined,
      brand_custom_text: undefined,
      product_type_custom_text:
        productTypes.find((p) => p.id === productTypeId)?.name.toLowerCase() === "other"
          ? productTypeCustomText
          : undefined,
    })
  }

  const handleProductTypeCustomTextChange = (value: string) => {
    setProductTypeCustomText(value)
    updateDataAction({
      brand_id: selectedBrand ?? undefined,
      theme_id: selectedTheme ?? undefined,
      product_type_id: selectedProductType ?? undefined,
      theme_custom_text: isThemeOther ? themeCustomText : undefined,
      brand_custom_text: undefined,
      product_type_custom_text: value.trim() || undefined,
    })
  }

  const handleThemeSearch = useCallback(
    (q: string) => {
      if (!selectedBrand) return
      setThemesLoading(true)
      fetchTaxonomy("theme", q, selectedBrand)
        .then((items) => setThemes(items))
        .finally(() => setThemesLoading(false))
    },
    [selectedBrand],
  )

  const useInlineBrandPicker = shouldUseClassicPicker(brands.length)
  const useInlineProductTypePicker = shouldUseClassicPicker(productTypes.length)

  const brandOptions = useMemo(() => toOptions(brands), [brands])
  const themeOptions = useMemo(() => toOptions(themes, true), [themes])
  const productTypeOptions = useMemo(() => toOptions(productTypes, true), [productTypes])
  const selectedBrandOption = useMemo(() => findOption(brandOptions, selectedBrand), [brandOptions, selectedBrand])
  const selectedThemeOption = useMemo(() => findOption(themeOptions, selectedTheme), [themeOptions, selectedTheme])
  const selectedProductTypeOption = useMemo(
    () => findOption(productTypeOptions, selectedProductType),
    [productTypeOptions, selectedProductType]
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading categories...</span>
      </div>
    )
  }

  const containerSpacing = compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"
  const buttonClasses = compact ? "w-full h-14 text-sm shadow-md rounded-full group" : "w-full h-16 text-base shadow-lg rounded-full group"
  const labelClass = "text-sm font-medium block mb-2 text-foreground"
  const inputClass = "mt-2 h-12 rounded-3xl " + creationFlowInputClass

  return (
    <div className={containerSpacing}>
      <motion.div
        className={compact ? "space-y-4" : "space-y-5"}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <div className={labelClass}>{tML("brandLabel")}</div>
          {useInlineBrandPicker ? (
            <CreationFlowInlinePicker
              items={brands.map((b) => ({ id: b.id, name: b.name }))}
              selectedId={selectedBrand}
              onSelect={handleBrandSelect}
            />
          ) : (
            <SearchableDropdown
              options={brandOptions}
              label="name"
              selectedValue={selectedBrandOption}
              onSelect={(option) => handleBrandSelect(option.code)}
              placeholder="Select brand..."
              showFlag={false}
              searchPlaceholder="Search brands..."
              emptyMessage="No brands found"
              onSearchQueryChange={(q) => {
                fetchTaxonomy("brand", q).then((items) => {
                  const legoOnly = items.filter(
                    (b) => b.slug?.toLowerCase() === "lego" || b.name.toLowerCase() === "lego",
                  )
                  setBrands(legoOnly)
                })
              }}
            />
          )}
        </div>

        {selectedBrand && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={labelClass}>{tML("themeLabel")}</div>
            <SearchableDropdown
              options={themeOptions}
              label="name"
              selectedValue={selectedThemeOption}
              onSelect={(option) => handleThemeSelect(option.code)}
              placeholder="Select theme..."
              showFlag={false}
              searchPlaceholder="Search themes..."
              emptyMessage="No themes found"
              loading={themesLoading}
              onSearchQueryChange={handleThemeSearch}
              sheetTitle="Select theme"
            />
            {isThemeOther && (
              <Input
                ref={customThemeInputRef}
                type="text"
                placeholder="Please specify your theme"
                value={themeCustomText}
                onChange={(e) => handleThemeCustomTextChange(e.target.value)}
                className={inputClass}
                style={{ fontSize: "16px" }}
              />
            )}
          </motion.div>
        )}

        {selectedBrand && selectedTheme && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={labelClass}>{tML("productTypeLabel")}</div>
            {useInlineProductTypePicker ? (
              <CreationFlowInlinePicker
                items={productTypes.map((p) => ({ id: p.id, name: p.name }))}
                selectedId={selectedProductType}
                onSelect={handleProductTypeSelect}
                otherLast
              />
            ) : (
              <SearchableDropdown
                options={productTypeOptions}
                label="name"
                selectedValue={selectedProductTypeOption}
                onSelect={(option) => handleProductTypeSelect(option.code)}
                placeholder="Select product type..."
                showFlag={false}
                searchPlaceholder="Search product types..."
                emptyMessage="No product types found"
                onSearchQueryChange={(q) => {
                  if (!selectedTheme) return
                  fetchTaxonomy("product_type", q, selectedTheme).then((items) => setProductTypes(items))
                }}
                sheetTitle="Select product type"
              />
            )}
            {isProductTypeOther && (
              <Input
                ref={productTypeCustomInputRef}
                type="text"
                placeholder="Specify product type"
                value={productTypeCustomText}
                onChange={(e) => handleProductTypeCustomTextChange(e.target.value)}
                className={inputClass}
                style={{ fontSize: "16px" }}
              />
            )}
          </motion.div>
        )}
      </motion.div>

      {!hideButton && (
        <div className="pt-6">
          <Button
            onClick={handleNext}
            disabled={!isValid}
            className={buttonClasses}
            size={compact ? "default" : "lg"}
          >
            Continue
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}
    </div>
  )
}
